import {
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { db } from "../config/firebase";

/**
 * Get top users by points for leaderboard
 * @param {number} topN - Number of top users to return (default: 50)
 * @returns {Promise<Array>} Array of users sorted by points (descending)
 */
export const getTopUsersByPoints = async (topN = 100) => {
  try {
    // Try to query with orderBy first
    const q = query(
      collection(db, "users"),
      orderBy("totalPoints", "desc"),
      limit(topN)
    );

    const querySnapshot = await getDocs(q);
    const users = querySnapshot.docs.map((doc, index) => {
      const data = doc.data();
      return {
        id: doc.id,
        rank: index + 1,
        address: data.address || doc.id,
        totalPoints: typeof data.totalPoints === 'number' ? data.totalPoints : 0,
        ...data,
      };
    });

    return users;
  } catch (error) {
    console.error("Error getting top users (trying fallback):", error);
    
    // If index doesn't exist, fallback to fetching all and sorting in memory
    try {
      const allUsersQuery = query(collection(db, "users"));
      const allSnapshot = await getDocs(allUsersQuery);
      
      const allUsers = allSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          address: data.address || doc.id,
          totalPoints: typeof data.totalPoints === 'number' ? data.totalPoints : 0,
          ...data,
        };
      });

      // Sort by points descending (users with 0 points will be at the end)
      allUsers.sort((a, b) => {
        const pointsA = a.totalPoints || 0;
        const pointsB = b.totalPoints || 0;
        if (pointsB !== pointsA) {
          return pointsB - pointsA;
        }
        // If points are equal, sort by address for consistency
        return (a.address || '').localeCompare(b.address || '');
      });

      // Add rank and limit
      return allUsers.slice(0, topN).map((user, index) => ({
        ...user,
        rank: index + 1,
      }));
    } catch (fallbackError) {
      console.error("Error in fallback query:", fallbackError);
      return [];
    }
  }
};

/**
 * Get user's rank in leaderboard
 * @param {string} address - Wallet address
 * @returns {Promise<number|null>} User's rank or null if not found
 */
export const getUserRank = async (address) => {
  try {
    if (!address) return null;

    const normalizedAddress = address.toLowerCase();
    
    // Fetch ALL users to find the rank
    try {
      const allUsersQuery = query(collection(db, "users"));
      const allSnapshot = await getDocs(allUsersQuery);
      
      const allUsers = allSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          address: data.address || doc.id,
          totalPoints: typeof data.totalPoints === 'number' ? data.totalPoints : 0,
          ...data,
        };
      });

      // Sort by points descending
      allUsers.sort((a, b) => {
        const pointsA = a.totalPoints || 0;
        const pointsB = b.totalPoints || 0;
        if (pointsB !== pointsA) {
          return pointsB - pointsA;
        }
        return (a.address || '').localeCompare(b.address || '');
      });
      
      const userIndex = allUsers.findIndex(
        (user) => (user.address?.toLowerCase() === normalizedAddress) || (user.id === normalizedAddress)
      );

      if (userIndex === -1) return null;
      return userIndex + 1;
    } catch (error) {
      console.error("Error in getUserRank:", error);
      return null;
    }
  } catch (error) {
    console.error("Error getting user rank:", error);
    return null;
  }
};

/**
 * Get users around a specific rank (for pagination or showing nearby users)
 * @param {number} rank - Target rank
 * @param {number} range - Number of users before and after to fetch
 * @returns {Promise<Array>} Array of users around the target rank
 */
export const getUsersAroundRank = async (rank, range = 5) => {
  try {
    const allUsers = await getTopUsersByPoints(1000);
    const startIndex = Math.max(0, rank - range - 1);
    const endIndex = Math.min(allUsers.length, rank + range);
    
    return allUsers.slice(startIndex, endIndex);
  } catch (error) {
    console.error("Error getting users around rank:", error);
    return [];
  }
};
